<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Platform Admin</h1>
          <p class="lede">Everyone on Paysol, from up here.</p>
        </div>
        <div class="head-actions">
          <button class="secondary" @click="load"><i class="mdi mdi-refresh"></i> Refresh</button>
        </div>
      </div>

      <!-- KPI STRIP -->
      <div class="kpi-strip" v-if="kpis">
        <div class="kpi">
          <div class="kpi-label">Tenants</div>
          <div class="kpi-val">{{ kpis.total_tenants }}</div>
          <div class="kpi-sub">{{ kpis.active_tenants }} active · {{ kpis.added_last_30d }} added last 30d</div>
        </div>
        <div class="kpi teal">
          <div class="kpi-label">MRR</div>
          <div class="kpi-val">KES {{ format(kpis.mrr_kes) }}</div>
          <div class="kpi-sub">Active subscriptions only</div>
        </div>
        <div class="kpi">
          <div class="kpi-label">GMV last 30d</div>
          <div class="kpi-val">KES {{ format(kpis.gmv_last_30d) }}</div>
          <div class="kpi-sub">{{ kpis.sales_last_30d }} sales processed</div>
        </div>
        <div class="kpi">
          <div class="kpi-label">GMV all-time</div>
          <div class="kpi-val">KES {{ format(kpis.gmv_all_time) }}</div>
          <div class="kpi-sub">{{ kpis.sales_all_time }} sales total</div>
        </div>
      </div>

      <!-- Sub-metric chips -->
      <div class="chip-row" v-if="kpis">
        <div class="chip-group">
          <span class="chip-title">By plan:</span>
          <span v-for="p in kpis.by_plan" :key="p.plan" :class="'chip plan-' + p.plan">{{ p.plan }}: {{ p.n }}</span>
        </div>
        <div class="chip-group">
          <span class="chip-title">By status:</span>
          <span v-for="s in kpis.by_status" :key="s.status" :class="'chip status-' + s.status">{{ s.status }}: {{ s.n }}</span>
        </div>
      </div>

      <!-- TABS -->
      <div class="tabs">
        <button :class="{tab: true, active: tab === 'tenants'}" @click="tab = 'tenants'">
          <i class="mdi mdi-domain"></i> Tenants ({{ tenants.length }})
        </button>
        <button :class="{tab: true, active: tab === 'invoices'}" @click="loadInvoices">
          <i class="mdi mdi-file-document-multiple"></i> Invoices
        </button>
        <button :class="{tab: true, active: tab === 'owners'}" @click="loadOwners">
          <i class="mdi mdi-shield-crown"></i> Platform Owners
        </button>
      </div>

      <!-- TENANTS TAB -->
      <div v-if="tab === 'tenants'" class="card">
        <div class="filter-row">
          <input v-model="search" placeholder="Search tenant name / email / phone…" style="flex:1" @keyup.enter="load" />
          <select v-model="statusFilter" @change="load" style="max-width:150px">
            <option value="">All status</option>
            <option value="trial">Trial</option>
            <option value="active">Active</option>
            <option value="past_due">Past due</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select v-model="planFilter" @change="load" style="max-width:150px">
            <option value="">All plans</option>
            <option value="trial">Trial</option>
            <option value="starter">Starter</option>
            <option value="growth">Growth</option>
            <option value="enterprise">Enterprise</option>
            <option value="custom">Custom</option>
          </select>
          <button class="secondary" @click="load">Search</button>
        </div>

        <table v-if="tenants.length">
          <thead>
            <tr>
              <th>Tenant</th><th>Type</th><th>Plan</th><th>Status</th>
              <th class="r">Users</th><th class="r">Sales</th><th class="r">GMV all-time</th><th class="r">GMV 30d</th>
              <th>Last sale</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in tenants" :key="t.id" @click="openTenant(t.id)" class="clickable" :class="{ inactive: !t.is_active }">
              <td>
                <strong>{{ t.name }}</strong>
                <div class="muted small">{{ t.email || t.phone || '—' }}{{ t.city ? ' · ' + t.city : '' }}</div>
              </td>
              <td class="muted small">{{ t.business_type }}</td>
              <td><span :class="'plan-pill plan-' + t.subscription_plan">{{ t.subscription_plan }}</span></td>
              <td><span :class="'status-pill status-' + t.subscription_status">{{ t.subscription_status }}</span></td>
              <td class="r">{{ t.user_count }}</td>
              <td class="r">{{ t.sale_count }}</td>
              <td class="r">KES {{ format(t.gmv) }}</td>
              <td class="r"><strong>KES {{ format(t.gmv_30d) }}</strong></td>
              <td class="muted small">{{ t.last_sale_at || 'Never' }}</td>
              <td><button class="secondary small">Open</button></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No tenants match.</p>
      </div>

      <!-- INVOICES TAB -->
      <div v-if="tab === 'invoices'" class="card">
        <div class="platform-paybill-row" v-if="platformPaybill">
          <i class="mdi mdi-cellphone-wireless"></i>
          <span>Collections paybill: <strong>{{ platformPaybill.shortcode }}</strong> ({{ platformPaybill.type }}, {{ platformPaybill.environment }})</span>
        </div>
        <div class="platform-paybill-row warn" v-else>
          <i class="mdi mdi-alert-circle-outline"></i>
          <span>
            <strong>No Paysol collections paybill set</strong> — tenants can't Pay-via-STK until you add one.
            <router-link to="/settings">Add a paybill in Settings</router-link> then use the <code>setPlatformPaybill</code> API to flag it.
          </span>
        </div>

        <div v-if="invoiceAggregate" class="kpi-strip" style="margin-bottom: 12px">
          <div class="kpi">
            <div class="kpi-label">Billed all-time</div>
            <div class="kpi-val">KES {{ format(invoiceAggregate.total_billed) }}</div>
          </div>
          <div class="kpi teal">
            <div class="kpi-label">Paid</div>
            <div class="kpi-val">KES {{ format(invoiceAggregate.total_paid) }}</div>
          </div>
          <div class="kpi" style="border-left-color: var(--paysol-warning);">
            <div class="kpi-label">Outstanding</div>
            <div class="kpi-val">KES {{ format(invoiceAggregate.total_outstanding) }}</div>
            <div class="kpi-sub">{{ invoiceAggregate.pending_count }} pending</div>
          </div>
        </div>

        <div class="filter-row">
          <input v-model="invSearch" placeholder="Search invoice # or tenant…" style="flex:1" @keyup.enter="loadInvoices" />
          <select v-model="invStatusFilter" @change="loadInvoices" style="max-width:140px">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="void">Void</option>
          </select>
          <button class="secondary" @click="loadInvoices">Search</button>
          <button class="primary" @click="showGenerate = true">Generate for period…</button>
        </div>

        <table v-if="invoices.length">
          <thead>
            <tr>
              <th>Invoice #</th><th>Tenant</th><th>Plan</th><th>Period</th>
              <th class="r">Amount</th><th>Status</th><th>Paid</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inv in invoices" :key="inv.id">
              <td><code>{{ inv.invoice_no }}</code></td>
              <td>{{ inv.tenant_name }}</td>
              <td><span :class="'plan-pill plan-' + (inv.plan_snapshot || 'trial')">{{ inv.plan_snapshot || '—' }}</span></td>
              <td class="muted small">{{ inv.period_start }} → {{ inv.period_end }}</td>
              <td class="r"><strong>KES {{ format(inv.amount) }}</strong></td>
              <td><span :class="'inv-pill ' + inv.status">{{ inv.status }}</span></td>
              <td class="muted small">{{ inv.paid_at || (inv.status === 'void' ? 'VOIDED' : '—') }}</td>
              <td>
                <button v-if="inv.status === 'pending'" class="primary small" @click="openMarkPaid(inv)">Mark paid</button>
                <button v-if="inv.status === 'pending'" class="danger small" @click="voidInvoice(inv)" style="margin-left:4px">Void</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No invoices yet. Click <strong>Generate for period</strong> to issue the first ones.</p>
      </div>

      <!-- OWNERS TAB -->
      <div v-if="tab === 'owners'" class="card">
        <p class="muted small">Platform owners have cross-tenant access. Keep this list very short.</p>
        <table v-if="owners.length">
          <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Home tenant</th><th>Last login</th><th></th></tr></thead>
          <tbody>
            <tr v-for="o in owners" :key="o.id">
              <td><strong>{{ o.name }}</strong></td>
              <td>{{ o.email }}</td>
              <td>{{ o.phone }}</td>
              <td class="muted">{{ o.tenant_name }}</td>
              <td class="muted small">{{ o.last_login_at || 'Never' }}</td>
              <td>
                <button class="danger small" @click="revokeOwner(o)" :disabled="o.id === myId">
                  {{ o.id === myId ? '(you)' : 'Revoke' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div style="margin-top:16px; padding-top:14px; border-top:1px solid var(--paysol-line);">
          <strong>Grant new platform owner</strong>
          <p class="muted small">Enter the user id of an existing tenant user to promote them. Grant sparingly.</p>
          <div style="display:flex; gap:8px; margin-top:8px;">
            <input type="number" v-model.number="grantUserId" placeholder="user id" style="max-width:120px" />
            <button class="primary" @click="grantOwner" :disabled="!grantUserId">Grant</button>
          </div>
          <p v-if="grantMsg" :class="grantOk ? 'ok' : 'err'" style="margin-top:6px">{{ grantMsg }}</p>
        </div>
      </div>
    </div>

    <!-- GENERATE INVOICES MODAL -->
    <div v-if="showGenerate" class="modal-back" @click.self="showGenerate=false">
      <div class="modal card">
        <h3>Generate invoices</h3>
        <p class="muted small">Issues invoices for every active-plan tenant that doesn't already have one for this period. Duplicates are skipped safely.</p>
        <div class="row-2">
          <div><label>Period start</label><input type="date" v-model="genForm.periodStart" /></div>
          <div><label>Period end</label><input type="date" v-model="genForm.periodEnd" /></div>
        </div>
        <div class="modal-actions">
          <button class="secondary" @click="showGenerate=false">Cancel</button>
          <button class="primary" @click="generateAll" :disabled="generating">
            {{ generating ? 'Generating…' : 'Generate all active-plan tenants' }}
          </button>
        </div>
        <p v-if="genMsg" :class="genOk ? 'ok' : 'err'" style="margin-top:8px;">{{ genMsg }}</p>
      </div>
    </div>

    <!-- MARK PAID MODAL -->
    <div v-if="markInv" class="modal-back" @click.self="markInv=null">
      <div class="modal card">
        <h3>Mark {{ markInv.invoice_no }} as paid</h3>
        <p class="muted small">Records the payment against this invoice. Tenant subscription flips to <strong>active</strong> if it was past-due.</p>
        <label>Payment method</label>
        <select v-model="markForm.paymentMethod">
          <option value="mpesa">M-Pesa</option>
          <option value="bank_transfer">Bank transfer</option>
          <option value="cash">Cash</option>
          <option value="manual">Manual / other</option>
        </select>
        <label>Reference (e.g. M-Pesa code)</label>
        <input v-model="markForm.paymentRef" placeholder="RGX123ABC" />
        <label>Paid at (optional — defaults to now)</label>
        <input type="date" v-model="markForm.paidAt" />
        <div class="modal-actions">
          <button class="secondary" @click="markInv=null">Cancel</button>
          <button class="primary" @click="markPaid" :disabled="marking">{{ marking ? 'Marking…' : 'Mark paid' }}</button>
        </div>
      </div>
    </div>

    <!-- TENANT DETAIL MODAL -->
    <div v-if="detail" class="modal-back" @click.self="detail=null">
      <div class="modal card wide">
        <div class="d-head">
          <div>
            <h3>{{ detail.tenant.name }}</h3>
            <p class="muted small">
              #{{ detail.tenant.id }} · {{ detail.tenant.business_type }} · {{ detail.tenant.email || '—' }}
              · <span :class="'status-pill status-' + detail.tenant.subscription_status">{{ detail.tenant.subscription_status }}</span>
              · <span :class="'plan-pill plan-' + detail.tenant.subscription_plan">{{ detail.tenant.subscription_plan }}</span>
              <span v-if="!detail.tenant.is_active" class="status-pill status-cancelled" style="margin-left:6px">DEACTIVATED</span>
            </p>
          </div>
          <button class="secondary" @click="detail=null">Close</button>
        </div>

        <div class="d-kpis">
          <div><span>Users</span><strong>{{ detail.users.length }}</strong></div>
          <div><span>Shops</span><strong>{{ detail.shops.length }}</strong></div>
          <div><span>Sales last 30d</span><strong>{{ detail.activity_30d.sales_30d }}</strong></div>
          <div><span>GMV last 30d</span><strong>KES {{ format(detail.activity_30d.gmv_30d) }}</strong></div>
          <div><span>Public slug</span><strong style="font-family:'JetBrains Mono',monospace; font-size:11px">{{ detail.tenant.public_slug || '—' }}</strong></div>
        </div>

        <h4>Subscription</h4>
        <div class="sub-form">
          <div>
            <label>Plan</label>
            <select v-model="subForm.plan">
              <option value="trial">Trial</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="enterprise">Enterprise</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label>Monthly amount (KES)</label>
            <input type="number" v-model.number="subForm.amount" min="0" step="100" />
          </div>
          <div>
            <label>Status</label>
            <select v-model="subForm.status">
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="past_due">Past due</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        <label>Notes</label>
        <input v-model="subForm.notes" />
        <div style="display:flex; gap:8px; margin-top:10px;">
          <button class="primary" @click="saveSubscription" :disabled="savingSub">
            {{ savingSub ? 'Saving…' : 'Save subscription' }}
          </button>
          <button :class="detail.tenant.is_active ? 'danger' : 'primary'" @click="toggleActive">
            {{ detail.tenant.is_active ? 'Deactivate tenant' : 'Activate tenant' }}
          </button>
        </div>
        <p v-if="subMsg" :class="subOk ? 'ok' : 'err'" style="margin-top:8px;">{{ subMsg }}</p>

        <h4 style="margin-top:20px;">Users ({{ detail.users.length }})</h4>
        <table class="small-table">
          <thead><tr><th>ID</th><th>Name</th><th>Role</th><th>Email</th><th>Flags</th><th>Last login</th></tr></thead>
          <tbody>
            <tr v-for="u in detail.users" :key="u.id">
              <td class="muted small">{{ u.id }}</td>
              <td>{{ u.name }}</td>
              <td>{{ u.role }}</td>
              <td class="muted small">{{ u.email || '—' }}</td>
              <td>
                <span v-if="u.is_platform_owner" class="chip status-active">platform</span>
                <span v-if="u.is_service_provider" class="chip status-active">provider</span>
                <span v-if="!u.is_active" class="chip status-cancelled">inactive</span>
              </td>
              <td class="muted small">{{ u.last_login_at || 'Never' }}</td>
            </tr>
          </tbody>
        </table>

        <h4 style="margin-top:20px;">Recent sales (last 20)</h4>
        <table class="small-table" v-if="detail.recent_sales.length">
          <thead><tr><th>Receipt</th><th>Status</th><th class="r">Total</th><th>When</th></tr></thead>
          <tbody>
            <tr v-for="s in detail.recent_sales" :key="s.id">
              <td><code>{{ s.receipt_no }}</code></td>
              <td>{{ s.status }}</td>
              <td class="r">KES {{ format(s.total) }}</td>
              <td class="muted small">{{ s.created_at }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted small">No sales yet.</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

export default {
  name: 'PlatformAdmin',
  components: { Navigation },
  data() {
    return {
      kpis: null,
      tenants: [],
      owners: [],
      tab: 'tenants',
      search: '', statusFilter: '', planFilter: '',
      detail: null,
      subForm: { plan: 'trial', amount: 0, status: 'trial', notes: '' },
      savingSub: false, subMsg: '', subOk: false,
      grantUserId: null, grantMsg: '', grantOk: false,
      myId: Number(sessionStorage.getItem('userId') || 0),
      // Invoices
      invoices: [], invoiceAggregate: null, invSearch: '', invStatusFilter: '',
      platformPaybill: null,
      showGenerate: false, generating: false, genMsg: '', genOk: false,
      genForm: {
        periodStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10),
        periodEnd:   new Date(new Date().getFullYear(), new Date().getMonth()+1, 0).toISOString().slice(0,10),
      },
      markInv: null, marking: false,
      markForm: { paymentMethod: 'mpesa', paymentRef: '', paidAt: '' },
    };
  },
  async mounted() { await this.load(); },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    async load() {
      const [k, t] = await Promise.all([
        execute({ function: 'getPlatformKpis' }, paysol),
        execute({ function: 'listAllTenants', search: this.search, status: this.statusFilter, plan: this.planFilter }, paysol),
      ]);
      if (k.data.success) this.kpis = k.data.data;
      if (t.data.success) this.tenants = t.data.data;
    },
    async loadOwners() {
      this.tab = 'owners';
      const { data } = await execute({ function: 'listPlatformOwners' }, paysol);
      if (data.success) this.owners = data.data;
    },
    async loadInvoices() {
      this.tab = 'invoices';
      const [invRes, ppRes] = await Promise.all([
        execute({ function: 'listAllPlatformInvoices', search: this.invSearch, status: this.invStatusFilter }, paysol),
        execute({ function: 'getPlatformPaybill' }, paysol),
      ]);
      if (invRes.data.success) { this.invoices = invRes.data.data.invoices; this.invoiceAggregate = invRes.data.data.aggregate; }
      if (ppRes.data.success && ppRes.data.data) this.platformPaybill = ppRes.data.data;
      else this.platformPaybill = null;
    },
    async generateAll() {
      this.generating = true; this.genMsg = '';
      try {
        const { data } = await execute({ function: 'generatePlatformInvoicesAll', ...this.genForm }, paysol);
        this.genOk = data.success;
        this.genMsg = data.success ? data.message : ('Failed: ' + data.message);
        if (data.success) { await this.loadInvoices(); }
      } finally { this.generating = false; }
    },
    openMarkPaid(inv) {
      this.markInv = inv;
      this.markForm = { paymentMethod: 'mpesa', paymentRef: '', paidAt: '' };
    },
    async markPaid() {
      this.marking = true;
      try {
        const { data } = await execute({ function: 'markPlatformInvoicePaid', id: this.markInv.id, ...this.markForm }, paysol);
        if (!data.success) { alert(data.message); return; }
        this.markInv = null;
        await this.loadInvoices();
        await this.load(); // MRR may change if a past-due tenant flipped active
      } finally { this.marking = false; }
    },
    async voidInvoice(inv) {
      const reason = prompt(`Void ${inv.invoice_no}? Reason:`);
      if (reason === null) return;
      const { data } = await execute({ function: 'voidPlatformInvoice', id: inv.id, reason }, paysol);
      if (data.success) await this.loadInvoices();
      else alert(data.message);
    },
    async openTenant(id) {
      const { data } = await execute({ function: 'getTenantDetail', id }, paysol);
      if (data.success) {
        this.detail = data.data;
        this.subForm = {
          plan: data.data.tenant.subscription_plan,
          amount: Number(data.data.tenant.subscription_amount),
          status: data.data.tenant.subscription_status,
          notes: data.data.tenant.subscription_notes || '',
        };
        this.subMsg = '';
      }
    },
    async saveSubscription() {
      this.savingSub = true; this.subMsg = '';
      try {
        const { data } = await execute({ function: 'updateTenantSubscription', id: this.detail.tenant.id, ...this.subForm }, paysol);
        this.subOk = data.success;
        this.subMsg = data.success ? 'Saved.' : ('Failed: ' + data.message);
        if (data.success) { await this.load(); await this.openTenant(this.detail.tenant.id); }
      } finally { this.savingSub = false; }
    },
    async toggleActive() {
      const action = this.detail.tenant.is_active ? 'Deactivate' : 'Activate';
      if (!confirm(`${action} ${this.detail.tenant.name}?`)) return;
      const { data } = await execute({ function: 'toggleTenantActive', id: this.detail.tenant.id }, paysol);
      if (data.success) { alert(data.message); await this.load(); await this.openTenant(this.detail.tenant.id); }
    },
    async grantOwner() {
      this.grantMsg = '';
      const { data } = await execute({ function: 'grantPlatformOwner', userId: this.grantUserId }, paysol);
      this.grantOk = data.success;
      this.grantMsg = data.success ? data.message : ('Failed: ' + data.message);
      if (data.success) { this.grantUserId = null; await this.loadOwners(); }
    },
    async revokeOwner(o) {
      if (!confirm(`Revoke platform-owner from ${o.name}?`)) return;
      const { data } = await execute({ function: 'revokePlatformOwner', userId: o.id }, paysol);
      if (data.success) await this.loadOwners();
      else alert(data.message);
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1400px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }

.kpi-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-bottom: 12px; }
.kpi { padding: 16px 18px; background: var(--paysol-card); border: 1px solid var(--paysol-line); border-left: 3px solid var(--paysol-muted); border-radius: 8px; }
.kpi.teal { border-left-color: var(--paysol-teal); }
.kpi-label { color: var(--paysol-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.kpi-val { color: var(--paysol-text); font-size: 24px; font-weight: 700; margin-top: 3px; }
.kpi-sub { color: var(--paysol-muted); font-size: 11px; margin-top: 4px; }

.chip-row { display: flex; gap: 20px; margin-bottom: 20px; flex-wrap: wrap; padding: 10px 14px; background: var(--paysol-bg-elev); border-radius: 6px; }
.chip-group { display: flex; gap: 6px; align-items: center; }
.chip-title { color: var(--paysol-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.chip { padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; background: var(--paysol-card); color: var(--paysol-text); border: 1px solid var(--paysol-line); }
.chip.plan-starter    { border-color: #4FC3F7; color: #4FC3F7; }
.chip.plan-growth     { border-color: var(--paysol-teal); color: var(--paysol-teal); }
.chip.plan-enterprise { border-color: var(--paysol-orange); color: var(--paysol-orange); }
.chip.status-active   { background: rgba(16,185,129,.15); color: var(--paysol-success); border-color: rgba(16,185,129,.3); }
.chip.status-past_due { background: rgba(245,158,11,.15); color: var(--paysol-warning); border-color: rgba(245,158,11,.3); }
.chip.status-cancelled{ background: rgba(239,68,68,.15); color: var(--paysol-danger); border-color: rgba(239,68,68,.3); }

.tabs { display: flex; gap: 4px; padding-bottom: 8px; margin-bottom: 16px; border-bottom: 1px solid var(--paysol-line); }
button.tab { background: transparent; color: var(--paysol-muted); padding: 10px 16px; border-radius: 6px 6px 0 0; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; border-bottom: 2px solid transparent; }
button.tab.active { color: var(--paysol-teal); border-bottom-color: var(--paysol-teal); background: var(--paysol-teal-glow); }

.filter-row { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; }
.clickable { cursor: pointer; }
.inactive td { opacity: 0.5; }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 40px 0; }
button.small { padding: 4px 10px; font-size: 11px; }

.plan-pill, .status-pill {
  display: inline-block; padding: 3px 8px; border-radius: 3px; font-size: 10px;
  font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
}
.plan-pill { background: var(--paysol-bg-elev); color: var(--paysol-muted); }
.plan-pill.plan-starter    { background: rgba(79,195,247,.15); color: #4FC3F7; }
.plan-pill.plan-growth     { background: var(--paysol-teal-glow); color: var(--paysol-teal); }
.plan-pill.plan-enterprise { background: rgba(229,165,102,.15); color: var(--paysol-orange); }
.plan-pill.plan-custom     { background: rgba(109,94,189,.15); color: #6D5EBD; }
.status-pill.status-trial     { background: rgba(139,164,196,.15); color: var(--paysol-muted); }
.status-pill.status-active    { background: rgba(16,185,129,.15); color: var(--paysol-success); }
.status-pill.status-past_due  { background: rgba(245,158,11,.15); color: var(--paysol-warning); }
.status-pill.status-cancelled { background: rgba(239,68,68,.15); color: var(--paysol-danger); }

.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 500px; max-height: 90vh; overflow-y: auto; }
.modal.wide { max-width: 900px; }
.d-head { display: flex; justify-content: space-between; align-items: start; margin-bottom: 16px; }
.d-head h3 { margin: 0 0 4px; color: var(--paysol-teal); }
.d-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 8px; padding: 12px; background: var(--paysol-bg-elev); border-radius: 6px; margin-bottom: 16px; }
.d-kpis div { display: flex; flex-direction: column; gap: 3px; }
.d-kpis span { color: var(--paysol-muted); font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
.d-kpis strong { color: var(--paysol-text); font-size: 14px; }
h4 { color: var(--paysol-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 8px; }
.sub-form { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 8px; }
.small-table { font-size: 12px; }
.small-table td, .small-table th { padding: 6px 10px; }
code { background: var(--paysol-bg-elev); padding: 2px 6px; border-radius: 3px; color: var(--paysol-teal); font-size: 11px; }
.ok { color: var(--paysol-success); font-size: 12px; }
.err { color: var(--paysol-danger); font-size: 12px; }
.inv-pill { display: inline-block; padding: 3px 8px; border-radius: 3px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.inv-pill.pending { background: rgba(245,158,11,.15); color: var(--paysol-warning); }
.inv-pill.paid    { background: rgba(16,185,129,.15); color: var(--paysol-success); }
.inv-pill.void    { background: rgba(139,164,196,.15); color: var(--paysol-muted); text-decoration: line-through; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.platform-paybill-row {
  display: flex; align-items: center; gap: 10px;
  background: var(--paysol-teal-glow); border: 1px solid var(--paysol-teal);
  padding: 10px 14px; border-radius: 6px; margin-bottom: 12px;
  color: var(--paysol-teal); font-size: 13px;
}
.platform-paybill-row.warn { background: rgba(245,158,11,.1); border-color: var(--paysol-warning); color: var(--paysol-warning); }
.platform-paybill-row.warn a { color: var(--paysol-warning); text-decoration: underline; }
.platform-paybill-row i { font-size: 18px; }
</style>
